import { confirmable } from 'react-confirm';
import { toast } from 'react-toastify';
import StackedModal from './stacked_modal';
import { useTranslation } from 'react-i18next';
import { Button, DropZone, Icon, IndexTable } from '@shopify/polaris';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { RHFAPIAutocompleteV2 } from '../hook_form/rhf_api_autocomplete_v2';
import FormProvider from '../hook_form/form_provider';
import { useForm } from 'react-hook-form';
import { isEmpty } from 'src/utils/type_check';
import FixedTable from '../table/fixed_table';
import useTable from 'src/hooks/use_table';
import { AlertTriangleIcon, CheckIcon } from '@shopify/polaris-icons';
import { utils, writeFile } from 'xlsx';
import { getInboundWithoutPoExcelSample, importInboundWithoutPoExcel } from 'src/api/inbound_api';
import { useGetLocations } from 'src/api/location_api';
import {
  useGetFeatureSettingReference,
  useGetProductCodeTypes,
  useGetReferenceCustomers,
} from 'src/api/reference_api';
import { useGetMe } from 'src/api/user_api';
import { getErrorMessage } from 'src/utils/error_message';
import { getUserName, requiredField } from 'src/utils/format_data';
import { chooseLan } from 'src/utils/language_code';
import { fDateTime } from 'src/utils/format_time';
import { Badge } from '../badge/badge';
import { LogInAlert } from '../alert/alert';

const FAILED_TABLE_MAX_HEIGHT = 392;

const FAILURE_FILE_NAME = 'inbound_without_po_import_failures_';

const FEATURE_KEY = 'ExcelInboundWithoutPo';

const alertStyle = {
  paddingY: 0,
  alignItems: 'center',
  '& .MuiAlert-icon': { alignItems: 'center', paddingY: 0, color: 'inherit' },
  '& .MuiAlert-action': { alignItems: 'center', paddingTop: 0 },
};

function InboundWithoutPoImportDialog({ show, proceed, locationId }) {
  const { t } = useTranslation(['inbound', 'excel', 'placeholder', 'common']);
  const { customerId, isAdmin } = useGetMe();
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [executed, setExecuted] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [isRunning, setIsRunning] = useState(false);

  const methods = useForm({
    defaultValues: {
      locationId: locationId,
      userId: customerId,
      productCodeType: '',
    },
  });

  const { watch } = methods;

  const userId = watch('userId');
  const { searchResults: featureSetting } = useGetFeatureSettingReference({
    featureKey: isEmpty(userId) ? '' : FEATURE_KEY,
    userId: userId,
    locationId: locationId,
  });
  const isFeatureDisabled = featureSetting?.settingValue?.enabled === false;
  const productCodeType = watch('productCodeType');
  const errors = report?.errors ?? [];

  const runImport = useCallback(
    async (validateOnly, values, excelFile) => {
      setWarningMessage('');
      setIsRunning(true);
      try {
        const res = await importInboundWithoutPoExcel({
          userId: values.userId,
          locationId: locationId,
          productCodeType: values.productCodeType,
          excelFile: excelFile,
          validateOnly: validateOnly,
        });
        if (!res.isSuccess) throw Error();
        setReport(res.result);
        if (!validateOnly) {
          if ((res.result.errors ?? []).length === 0) {
            toast.success(t('excel:import.executed', { total: res.result.succeededRows }));
            proceed(true);
            return;
          }
          setExecuted(true);
        }
      } catch (error) {
        setWarningMessage(getErrorMessage(error));
      }
      setIsRunning(false);
    },
    [locationId, proceed, t],
  );

  const changeFile = (next) => {
    setFile(next);
    setReport(null);
    setExecuted(false);
    setWarningMessage('');
  };

  const handleDropZoneDrop = (_dropFiles, acceptedFiles) => {
    changeFile(acceptedFiles[0]);
  };

  const renderLocation = (
    <Box>
      <RHFAPIAutocompleteV2
        displayName={t('excel:import.location')}
        name="locationId"
        useOptions={useGetLocations}
        params={{ pageSize: 100, pageNumber: 1, locationId: locationId }}
        toOption={(r) => ({
          label: chooseLan(r.locationNameLocal, r.locationNameEnglish),
          value: r.locationId,
        })}
        searchParamName={'q'}
        disabled
        hideSearchIcon
      />
    </Box>
  );

  const renderCustomer = (
    <Box>
      <RHFAPIAutocompleteV2
        displayName={requiredField(t('excel:import.customer'))}
        name="userId"
        placeholder={t('placeholder:customer')}
        useOptions={useGetReferenceCustomers}
        params={{ locationId }}
        toOption={(r) => ({
          label: getUserName(r),
          value: r.userId,
        })}
        searchParamName={'search'}
        resetSearchOnOpen
        onChange={() => setReport(null)}
      />
    </Box>
  );

  const renderProductCodeType = (
    <Box>
      <RHFAPIAutocompleteV2
        displayName={requiredField(t('excel:import.upload_code_type'))}
        name="productCodeType"
        placeholder={t('placeholder:upload_code_type')}
        disabled={isFeatureDisabled}
        useOptions={useGetProductCodeTypes}
        toOption={(r) => ({
          label: chooseLan(r.productCodeTypeNameLocal, r.productCodeTypeNameEnglish),
          value: r.productCodeType,
        })}
        resetSearchOnOpen
        onChange={() => setReport(null)}
      />
    </Box>
  );

  const dropFile =
    file === null ? (
      <DropZone.FileUpload
        actionTitle={t('excel:add')}
        actionHint={`${t('excel:accepts')} .xlsx`}
      />
    ) : (
      <Stack
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        direction={'row'}
        px={2}
        py={4}
      >
        <Typography variant="body_md_semibold">{file?.name}</Typography>
        <Button onClick={() => changeFile(null)}>{t('excel:replace')}</Button>
      </Stack>
    );

  const renderFeatureAlert = isFeatureDisabled && (
    <LogInAlert
      severity="error"
      sx={alertStyle}
      errorMsg={t('excel:import.feature_disabled')}
    />
  );

  const renderAlert = !isEmpty(warningMessage) && (
    <LogInAlert
      onClose={() => setWarningMessage('')}
      severity="error"
      sx={alertStyle}
      errorMsg={warningMessage}
    />
  );

  const resultMessage = () => {
    if (executed) {
      return t('excel:import.executed', { total: report.succeededRows });
    }
    if (report.errorRows === 0) {
      return t('excel:import.ready', { total: report.totalRows });
    }
    return t('excel:import.validated_with_errors', {
      total: report.totalRows,
      fail: report.errorRows,
    });
  };

  const renderResultAlert = report ? (
    errors.length > 0 ? (
      <LogInAlert
        severity="error"
        sx={alertStyle}
        errorMsg={resultMessage()}
      />
    ) : (
      <Alert
        icon={<Icon source={CheckIcon} />}
        sx={alertStyle}
      >
        {resultMessage()}
      </Alert>
    )
  ) : null;

  const exportFailures = () => {
    const rows = errors.map((e) => ({
      [t('excel:row_number')]: e.rowNumber,
      [t('excel:import.column')]: isEmpty(e.column) ? '' : e.column,
      [t('excel:import.value')]: e.value,
      [t('excel:error_cause')]: e.message,
    }));
    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(rows), 'errors');
    writeFile(wb, `${FAILURE_FILE_NAME}${fDateTime(new Date())}.xlsx`);
  };

  const renderFailedHeader = (
    <Stack
      direction={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
    >
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1}
      >
        <Typography
          variant={'body_md_semibold'}
          sx={{ color: 'var(--p-color-text-critical)' }}
        >
          {t('excel:import.failed')}
        </Typography>
        <Badge tone={'critical'}>{errors.length}</Badge>
      </Stack>
      <Button onClick={exportFailures}>{t('excel:import.export_failures')}</Button>
    </Stack>
  );

  const table = useTable({
    data: errors,
    headers: [
      { title: '' },
      { title: t('excel:row_number') },
      { title: t('excel:import.column') },
      { title: t('excel:import.value') },
      { title: t('excel:error_cause') },
    ],
    showPagination: false,
    showRowsPerPage: false,
    rowMarkup: ({ data }) => {
      return data.map(({ rowNumber, column, value, message }, index) => (
        <IndexTable.Row
          id={index}
          key={index}
          position={index}
          onClick={() => {}}
        >
          <Box
            component={IndexTable.Cell}
            sx={{ width: '40px' }}
          >
            <Icon
              source={AlertTriangleIcon}
              tone="critical"
            />
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '10%' }}
          >
            {rowNumber}
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '20%' }}
          >
            {isEmpty(column) ? '' : column}
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '25%' }}
          >
            {value}
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '40%' }}
          >
            {message}
          </Box>
        </IndexTable.Row>
      ));
    },
  });

  const content = (
    <FormProvider methods={methods}>
      <Stack spacing={1.5}>
        {renderAlert}
        {renderFeatureAlert}
        {renderResultAlert}
        {renderLocation}
        {isAdmin && renderCustomer}
        {renderProductCodeType}
        <DropZone
          onDrop={handleDropZoneDrop}
          allowMultiple={false}
          variableHeight
          accept=".xlsx"
          disabled={executed || isFeatureDisabled}
        >
          {dropFile}
        </DropZone>
        {errors.length > 0 && (
          <>
            {renderFailedHeader}
            <FixedTable
              useTable={table}
              maxHeight={FAILED_TABLE_MAX_HEIGHT}
            />
          </>
        )}
      </Stack>
    </FormProvider>
  );

  const primaryAction = executed
    ? {
        content: t('common:button.done'),
        onAction: () => proceed(true),
      }
    : report === null
      ? {
          content: t('excel:import.verify'),
          onAction: () => runImport(true, { userId, productCodeType }, file),
          loading: isRunning,
          disabled:
            isFeatureDisabled || isEmpty(userId) || isEmpty(productCodeType) || file === null,
        }
      : {
          content: t('excel:import.upload'),
          onAction: () => runImport(false, { userId, productCodeType }, file),
          loading: isRunning,
          disabled: report.canExecute === false,
        };

  const footerLeftNode = (
    <Button
      variant="tertiary"
      onClick={async () => {
        await getInboundWithoutPoExcelSample();
      }}
    >
      <Typography
        variant={'body_md'}
        sx={{ color: 'link', textDecoration: 'underline' }}
      >
        {t('excel:download')}
      </Typography>
    </Button>
  );

  return (
    <StackedModal
      isOpen={show}
      title={t('common:button.import_without_po')}
      modalContentStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      content={content}
      primaryAction={primaryAction}
      footerLeftNode={footerLeftNode}
      onClose={() => {
        proceed(executed);
      }}
    />
  );
}

export default confirmable(InboundWithoutPoImportDialog);
